import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  FileText,
  Calculator,
  Hammer,
  MapPin,
  Wrench,
  Droplets,
  Layers,
  ClipboardCheck,
  Zap,
  Wind,
} from "lucide-react";
import { GUIDES } from "../data/guides";
import SEO from "../components/SEO";
import { SITE_URL } from "../lib/site";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "UpgradeCost",
      url: SITE_URL,
      logo: `${SITE_URL}/favicon.svg`,
      description:
        "Free bathroom remodel cost calculator with hidden-cost warnings and a printable contractor brief for U.S. homeowners.",
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: "UpgradeCost",
      publisher: { "@id": `${SITE_URL}/#organization` },
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/guides?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "WebApplication",
      name: "Bathroom Remodel Cost Calculator",
      url: `${SITE_URL}/calculator`,
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Any",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description:
        "Free interactive bathroom remodel cost calculator. Get a low/expected/high range, timeline, hidden-cost warnings, and a printable contractor brief.",
    },
  ],
};

const HERO_IMG =
  "https://images.pexels.com/photos/7045908/pexels-photo-7045908.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";

// Bar-chart data — costs scale against MAX so visualisation reads correctly.
const HIDDEN_COSTS = [
  { icon: Wrench, title: "Plumbing relocation", low: 1500, high: 6500, tone: "brand" },
  { icon: Droplets, title: "Water damage / mold", low: 800, high: 4500, tone: "clay" },
  { icon: Layers, title: "Subfloor repair", low: 600, high: 2800, tone: "sand" },
  { icon: ClipboardCheck, title: "Permits & disposal", low: 450, high: 1800, tone: "brand" },
  { icon: Zap, title: "Electrical upgrades", low: 400, high: 1900, tone: "clay" },
  { icon: Wind, title: "Ventilation upgrade", low: 250, high: 950, tone: "sand" },
];
const MAX_HIDDEN = 6500;

const TONE_BAR = {
  brand: "bg-brand",
  clay: "bg-clay",
  sand: "bg-sand-deep",
};
const TONE_TXT = {
  brand: "text-brand",
  clay: "text-clay",
  sand: "text-sand-deep",
};

const money = (n) => `$${n.toLocaleString()}`;

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Bathroom Remodel Cost Calculator"
        description="Free bathroom remodel cost calculator with hidden-cost warnings and a printable contractor brief. Get your real cost range in under 2 minutes."
        path="/"
        jsonLd={homeJsonLd}
      />
      {/* ───── HERO ─────
          Single-column text-forward layout. The bathroom photo is a smaller
          decorative card on the right, with a soft warm gradient mesh behind
          everything so the whole section feels integrated rather than split. */}
      <section className="relative overflow-hidden bg-mesh border-b border-line">
        {/* Decorative color blobs */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sand/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-brand-tint blur-3xl pointer-events-none" />

        <div className="container-x relative pt-16 lg:pt-20 pb-20 lg:pb-24">
          <p className="overline mb-6" data-testid="hero-overline">
            Bathroom remodel · planning tool
          </p>

          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.04]">
                Know what your bathroom remodel{" "}
                <em className="text-brand">should</em> cost — before a contractor walks in.
              </h1>
              <p className="mt-6 text-lg text-ink-muted max-w-xl leading-relaxed">
                Get a real cost range, a timeline, a list of hidden expenses, and a printable brief you can bring to every bid. Free, takes under two minutes.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/calculator"
                  data-testid="hero-cta-primary"
                  className="btn-primary text-base px-6 py-4"
                >
                  Get my estimate
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/guides/bathroom-remodel-cost"
                  data-testid="hero-cta-secondary"
                  className="text-sm font-medium text-ink hover:text-brand underline underline-offset-4 decoration-line"
                >
                  Or browse cost guides →
                </Link>
              </div>

              <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-ink-muted">
                <span className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-brand" /> No spam, no signup to start
                </span>
                <span className="flex items-center gap-2">
                  <FileText size={16} className="text-brand" /> Printable contractor brief
                </span>
                <span className="flex items-center gap-2">
                  <Wrench size={16} className="text-clay" /> Hidden-cost warnings
                </span>
              </div>
            </div>

            {/* Small decorative photo + stat card stack */}
            <div className="lg:col-span-4 relative" aria-hidden>
              <div className="relative aspect-square rounded-lg overflow-hidden border border-line max-w-[300px] ml-auto">
                <img
                  src={HERO_IMG}
                  alt=""
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-ink/10 via-transparent to-transparent pointer-events-none" />
              </div>
              <div className="surface-card p-4 max-w-[240px] -mt-8 ml-4 relative shadow-sm bg-surface">
                <p className="overline">Median U.S. range</p>
                <p className="font-display text-3xl mt-1.5">$7.5k – $42k</p>
                <p className="text-xs text-ink-muted mt-1.5">
                  Cosmetic refresh through full gut
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section className="container-x py-24" data-testid="how-it-works">
        <div className="max-w-2xl">
          <p className="overline mb-4">How it works</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Three steps. No sales calls.
          </h2>
        </div>

        <div className="mt-12 grid md:grid-cols-3 grid-blueprint border-l-0 border-t-0 md:border-l md:border-t">
          {[
            {
              icon: Calculator,
              n: "01",
              title: "Tell us your project",
              body: "Size, scope, finish, fixtures, and a ZIP. Two minutes, no email required to see your range.",
              accent: "text-brand",
            },
            {
              icon: Hammer,
              n: "02",
              title: "See your real cost range",
              body: "Low, expected, and high estimates. Labor vs materials breakdown. Timeline in weeks.",
              accent: "text-clay",
            },
            {
              icon: FileText,
              n: "03",
              title: "Get a contractor brief",
              body: "A clean PDF you can bring to every bid. Compare apples-to-apples, no gimmicks.",
              accent: "text-sand-deep",
            },
          ].map((s) => (
            <div key={s.n} className="p-8 lg:p-10 bg-surface relative">
              <div className="flex items-center justify-between mb-8">
                <s.icon className={s.accent} size={28} strokeWidth={1.6} />
                <span className="font-mono text-xs text-ink-faint">{s.n}</span>
              </div>
              <h3 className="font-display text-2xl mb-3">{s.title}</h3>
              <p className="text-ink-muted text-sm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ───── HIDDEN COSTS — Bar chart layout ───── */}
      <section
        className="bg-sand-wash border-y border-line"
        data-testid="hidden-costs-section"
      >
        <div className="container-x py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* Header */}
            <div className="lg:col-span-5">
              <p className="overline mb-4 text-clay">Hidden cost alerts</p>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                The line items your contractor's first bid won't include.
              </h2>
              <p className="mt-6 text-ink-muted leading-relaxed">
                Most homeowners blow their budget on issues discovered after demo starts. These are the six surprise categories we flag for every project.
              </p>

              {/* Big stat block — pure data, no broken photo */}
              <div className="mt-10 p-7 rounded-lg bg-ink text-white relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-clay/30 blur-2xl" />
                <p className="overline mb-3 text-sand">By the numbers</p>
                <p className="font-display text-6xl leading-none">
                  <span className="text-sand">1</span>
                  <span className="text-ink-faint mx-2">in</span>
                  <span className="text-sand">4</span>
                </p>
                <p className="text-sm leading-relaxed mt-3 text-white/80 max-w-[280px]">
                  bathroom remodels of partial scope or larger uncovers hidden water damage or rot during demo.
                </p>
              </div>
            </div>

            {/* Bar chart */}
            <div className="lg:col-span-7">
              <div className="surface-card p-7 lg:p-8">
                <div className="flex items-baseline justify-between mb-7">
                  <p className="font-display text-2xl">Typical surprise costs</p>
                  <p className="font-mono text-xs text-ink-faint">
                    Scaled to $6,500 max
                  </p>
                </div>

                <div className="space-y-5" data-testid="hidden-costs-chart">
                  {HIDDEN_COSTS.map((h) => {
                    const leftPct = (h.low / MAX_HIDDEN) * 100;
                    const widthPct = ((h.high - h.low) / MAX_HIDDEN) * 100;
                    return (
                      <div
                        key={h.title}
                        data-testid={`hidden-cost-row-${h.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="group"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <h.icon size={14} className={TONE_TXT[h.tone]} />
                          <p className="text-sm font-medium flex-1">
                            {h.title}
                          </p>
                          <p className="font-mono text-xs text-ink-muted">
                            {money(h.low)} – {money(h.high)}
                          </p>
                        </div>
                        <div className="relative h-2.5 bg-bg-alt rounded-full">
                          <div
                            className={`absolute top-0 h-full ${TONE_BAR[h.tone]} rounded-full transition-all group-hover:brightness-110`}
                            style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                          />
                          {/* Endpoint dots */}
                          <div
                            className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ${TONE_BAR[h.tone]} ring-2 ring-surface`}
                            style={{ left: `calc(${leftPct}% - 5px)` }}
                          />
                          <div
                            className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ${TONE_BAR[h.tone]} ring-2 ring-surface`}
                            style={{ left: `calc(${leftPct + widthPct}% - 5px)` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Scale ticks */}
                <div className="mt-6 pt-4 border-t border-line flex justify-between font-mono text-[10px] text-ink-faint">
                  <span>$0</span>
                  <span>$1.5k</span>
                  <span>$3k</span>
                  <span>$4.5k</span>
                  <span>$6.5k</span>
                </div>
              </div>

              <p className="mt-4 text-xs text-ink-muted leading-relaxed">
                Bars show the typical low → high range for each surprise cost. Most projects encounter at least 3 of these.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── GUIDES ───── */}
      <section className="container-x py-24" data-testid="home-guides">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
          <div className="max-w-xl">
            <p className="overline mb-4">Cost guides</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Research before you bid.
            </h2>
          </div>
          <Link
            to="/guides"
            className="btn-ghost"
            data-testid="home-view-all-guides"
          >
            View all guides
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {GUIDES.slice(0, 6).map((g, idx) => {
            const accents = [
              "text-brand",
              "text-clay",
              "text-sand-deep",
              "text-brand",
              "text-clay",
              "text-sand-deep",
            ];
            return (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                data-testid={`guide-card-${g.slug}`}
                className="surface-card overflow-hidden group hover:-translate-y-1 transition-transform hover:shadow-lg"
              >
                {g.image && (
                  <div className="aspect-[16/10] overflow-hidden bg-bg-alt">
                    <img
                      src={g.image}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.parentElement.style.display = "none";
                      }}
                    />
                  </div>
                )}
                <div className="p-6">
                  <div
                    className={`flex items-center gap-2 text-xs font-mono mb-3 ${accents[idx]}`}
                  >
                    <MapPin size={12} /> Cost guide
                  </div>
                  <h3 className="font-display text-2xl leading-snug group-hover:text-brand transition-colors">
                    {g.title}
                  </h3>
                  <p className="font-mono text-xs text-ink-muted mt-3">
                    {`$${(g.range.low / 1000).toFixed(1)}k – $${(g.range.high / 1000).toFixed(0)}k`}
                  </p>
                  <p className="text-sm text-ink-muted leading-relaxed mt-3 line-clamp-2">
                    {g.subtitle}
                  </p>
                  <p className="mt-4 text-sm font-medium text-brand inline-flex items-center gap-1.5">
                    Read guide <ArrowRight size={14} />
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="container-x pb-24" data-testid="final-cta-section">
        <div className="surface-card p-10 lg:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-tint blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-clay-tint blur-3xl" />
          <div className="relative">
            <p className="overline mb-4">Free · No signup to start</p>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight max-w-2xl mx-auto">
              Get your bathroom remodel estimate.
            </h2>
            <p className="mt-5 text-ink-muted max-w-lg mx-auto">
              Two minutes, real numbers, and a contractor brief you can actually use.
            </p>
            <Link
              to="/calculator"
              data-testid="final-cta-button"
              className="btn-primary mt-8 px-8 py-4"
            >
              Start estimating
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
